import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Skull, ArrowLeft, KeyRound, RefreshCw, PackageCheck, LogOut, Mail, Plus, Upload, GripVertical, Paperclip, UserPlus } from "lucide-react";
import { useAuth } from "../lib/auth";
import AdminDashboard from "../components/admin/AdminDashboard";
import AdminCustomers from "../components/admin/AdminCustomers";
import AdminMarketing from "../components/admin/AdminMarketing";
import AdminDossiers from "../components/admin/AdminDossiers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adminInput =
  "font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30";

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("atrocity-admin-key") || "");
  const [input, setInput] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [packingOnly, setPackingOnly] = useState(false);
  const [shippingId, setShippingId] = useState(null);
  const [trackingInput, setTrackingInput] = useState("");
  const [view, setView] = useState("orders");
  const [products, setProducts] = useState(null);
  const [priceInputs, setPriceInputs] = useState({});
  const [stockInputs, setStockInputs] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ title: "", category: "physical", price: "", badge: "NEW ISSUE", cover: "", description: "", preorder: false, ships_by: "", release_at: "", bundle_items: [] });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", badge: "", description: "" });
  const [retiringId, setRetiringId] = useState(null);
  const [admins, setAdmins] = useState(null);
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [noteEditing, setNoteEditing] = useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [refundingId, setRefundingId] = useState(null);
  const { user } = useAuth();
  const autoEntered = useRef(false);

  const adminCfg = () => ({ headers: { "X-Admin-Key": key }, withCredentials: true });

  const load = async (k) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders`, { headers: { "X-Admin-Key": k }, withCredentials: true });
      setOrders(r.data);
      if (k) {
        sessionStorage.setItem("atrocity-admin-key", k);
        setKey(k);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        if (k) toast.error("Wrong key, renegade.");
        sessionStorage.removeItem("atrocity-admin-key");
        setKey("");
        setOrders(null);
      } else {
        toast.error("The ledger wouldn't open. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) {
      load(key);
    } else if (user && user.role === "admin" && !autoEntered.current) {
      autoEntered.current = true;
      load("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const markShipped = async (id) => {
    try {
      await axios.patch(
        `${API}/admin/orders/${id}`,
        { status: "shipped", tracking: trackingInput.trim() || undefined },
        adminCfg()
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.id === id ? { ...o, status: "shipped", tracking: trackingInput.trim() || o.tracking } : o
        )
      );
      setShippingId(null);
      setTrackingInput("");
      toast.success("Marked as shipped. Another coffin dispatched.");
    } catch {
      toast.error("Couldn't update that order.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("atrocity-admin-key");
    setKey("");
    setOrders(null);
    setInput("");
  };

  const loadProducts = async () => {
    try {
      const r = await axios.get(`${API}/admin/products`, adminCfg());
      setProducts(r.data);
      setPriceInputs(Object.fromEntries(r.data.map((p) => [p.id, p.price.toFixed(2)])));
      setStockInputs(Object.fromEntries(r.data.map((p) => [p.id, p.stock == null ? "" : String(p.stock)])));
    } catch {
      toast.error("Couldn't load the rack.");
    }
  };

  const loadAdmins = async () => {
    try {
      const r = await axios.get(`${API}/admin/admins`, adminCfg());
      setAdmins(r.data);
    } catch {
      toast.error("Couldn't load the coven.");
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    setAddingAdmin(true);
    try {
      const r = await axios.post(`${API}/admin/admins`, adminForm, adminCfg());
      toast.success(
        r.data.status === "promoted"
          ? `${r.data.email} already had an account — it's been handed a ledger key.`
          : `${r.data.email} can now open the ledger.`
      );
      setAdminForm({ name: "", email: "", password: "" });
      loadAdmins();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't add that admin.");
    } finally {
      setAddingAdmin(false);
    }
  };

  const revokeAdmin = async (a) => {
    try {
      await axios.delete(`${API}/admin/admins/${a.id}`, adminCfg());
      toast.success(`${a.email} is a mere mortal again. Their account stays — the key is gone.`);
      loadAdmins();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't revoke that key.");
    } finally {
      setRevokingId(null);
    }
  };

  const savePrice = async (id) => {
    try {
      const r = await axios.patch(
        `${API}/admin/products/${id}`,
        { price: parseFloat(priceInputs[id]) },
        adminCfg()
      );
      setProducts((prev) => prev.map((p) => (p.id === id ? r.data.product : p)));
      toast.success(
        r.data.fans_notified > 0
          ? `Price dropped. ${r.data.fans_notified} fan${r.data.fans_notified === 1 ? "" : "s"} notified in blood-red ink.`
          : "Price updated."
      );
    } catch {
      toast.error("Couldn't update that price.");
    }
  };

  const saveEdit = async (id) => {
    try {
      const r = await axios.patch(
        `${API}/admin/products/${id}`,
        { title: editForm.title, badge: editForm.badge, description: editForm.description },
        adminCfg()
      );
      setProducts((prev) => prev.map((p) => (p.id === id ? r.data.product : p)));
      setEditingId(null);
      toast.success("Product rewritten in the ledger.");
    } catch {
      toast.error("Couldn't save those edits.");
    }
  };

  const retire = async (id, retired) => {
    try {
      const r = await axios.patch(
        `${API}/admin/products/${id}`,
        { retired },
        adminCfg()
      );
      setProducts((prev) => prev.map((p) => (p.id === id ? r.data.product : p)));
      setRetiringId(null);
      toast.success(retired ? "Retired from the rack. The shop no longer shows it." : "Back on the rack.");
    } catch {
      toast.error("Couldn't update that product.");
    }
  };

  const swapCover = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/admin/upload-cover`, fd, adminCfg());
      const r = await axios.patch(
        `${API}/admin/products/${id}`,
        { cover: up.data.url },
        adminCfg()
      );
      setProducts((prev) => prev.map((p) => (p.id === id ? r.data.product : p)));
      toast.success("Cover swapped. The rack wears new ink.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't swap that cover.");
    }
  };

  const dragId = useRef(null);

  const saveStock = async (id) => {
    const raw = (stockInputs[id] ?? "").toString().trim();
    try {
      const r = await axios.patch(
        `${API}/admin/products/${id}`,
        { stock: raw === "" ? null : parseInt(raw, 10) },
        adminCfg()
      );
      setProducts((prev) => prev.map((p) => (p.id === id ? r.data.product : p)));
      toast.success(raw === "" ? "Stock tracking off — unlimited." : `Stock set to ${raw}.`);
    } catch {
      toast.error("Couldn't set stock.");
    }
  };

  const attachFile = async (id, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/products/${id}/file`, fd, adminCfg());
      setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, file_path: r.data.file_path, file_name: r.data.file_name } : p)));
      toast.success("PDF chained to the product. Buyers get a download link.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't attach that file.");
    }
  };

  const onDropRow = async (targetId) => {
    const list = products || [];
    const from = list.findIndex((p) => p.id === dragId.current);
    const to = list.findIndex((p) => p.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setProducts(next);
    try {
      await axios.post(
        `${API}/admin/products/reorder`,
        { ordered_ids: next.map((p) => p.id) },
        adminCfg()
      );
      toast.success("Rack order saved. The shop wears it now.");
    } catch {
      toast.error("Couldn't save the order.");
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    if (!coverFile) {
      toast.error("Choose a cover image first.");
      return;
    }
    try {
      const fmt =
        addForm.category === "digital" ? "Digital PDF" : addForm.category === "collectible" ? "Art Print" : addForm.category === "bundle" ? "Bundle" : addForm.category === "giftcard" ? "Gift Card" : addForm.category === "mystery" ? "Mystery Grab" : "Physical Comic";
      const fd = new FormData();
      fd.append("file", coverFile);
      const up = await axios.post(`${API}/admin/upload-cover`, fd, adminCfg());
      const payload = {
        ...addForm,
        format: fmt,
        price: parseFloat(addForm.price),
        cover: up.data.url,
        ships_by: addForm.ships_by || undefined,
        release_at: addForm.release_at ? new Date(addForm.release_at).toISOString() : undefined,
        bundle_items: addForm.category === "bundle" ? addForm.bundle_items : [],
      };
      const r = await axios.post(`${API}/admin/products`, payload, adminCfg());
      let created = r.data.product;
      if (addForm.category === "digital" && pdfFile) {
        const pfd = new FormData();
        pfd.append("file", pdfFile);
        const pr = await axios.post(`${API}/admin/products/${created.id}/file`, pfd, adminCfg());
        created = { ...created, file_path: pr.data.file_path, file_name: pr.data.file_name };
      }
      if (addForm.category === "digital" && previewFiles.length > 0) {
        const vfd = new FormData();
        previewFiles.forEach((f) => vfd.append("files", f));
        const vr = await axios.post(`${API}/admin/products/${created.id}/previews`, vfd, adminCfg());
        created = { ...created, preview_pages: vr.data.preview_pages };
      }
      setProducts((prev) => [...(prev || []), created]);
      setPriceInputs((prev) => ({ ...prev, [created.id]: created.price.toFixed(2) }));
      setShowAdd(false);
      setAddForm({ title: "", category: "physical", price: "", badge: "NEW ISSUE", cover: "", description: "", preorder: false, ships_by: "", release_at: "", bundle_items: [] });
      setPdfFile(null);
      setPreviewFiles([]);
      setCoverFile(null);
      setCoverPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      toast.success(
        r.data.fans_notified > 0
          ? `Added. ${r.data.fans_notified} fan${r.data.fans_notified === 1 ? "" : "s"} heard the news.`
          : addForm.category === "digital" && !pdfFile
            ? "Added to the rack — attach its PDF from the product row before buyers come."
            : "Added to the rack."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add that product.");
    }
  };

  const saveNote = async (id) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}/note`, { note: noteInput }, adminCfg());
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, admin_note: noteInput.trim() } : o)));
      setNoteEditing(null);
      toast.success("Note carved.");
    } catch {
      toast.error("Couldn't save that note.");
    }
  };

  const refundOrder = async (id) => {
    try {
      await axios.post(`${API}/admin/orders/${id}/refund`, {}, adminCfg());
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: "refunded" } : o)));
      toast.success("Refunded. Stock returned to the rack, buyer notified.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refund failed.");
    } finally {
      setRefundingId(null);
    }
  };

  const packingCount = orders ? orders.filter((o) => o.contains_physical && o.status === "paid").length : 0;

  const sorted = orders
    ? [...orders]
        .filter((o) => !packingOnly || (o.contains_physical && o.status === "paid"))
        .sort((a, b) => {
          const aNeeds = a.contains_physical && a.status === "paid" ? 0 : 1;
          const bNeeds = b.contains_physical && b.status === "paid" ? 0 : 1;
          return aNeeds - bNeeds;
        })
    : [];

  return (
    <div data-testid="admin-page" className="relative min-h-screen bg-void newsprint-grain">
      <div className="absolute inset-0 blood-vignette pointer-events-none" />
      <header className="relative border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="admin-brand" className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <div className="flex items-center gap-4">
            {orders && (
              <button
                data-testid="admin-logout-button"
                onClick={logout}
                className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
              >
                <LogOut size={14} /> Lock the Ledger
              </button>
            )}
            <a
              href="/"
              data-testid="admin-back-link"
              className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
            >
              <ArrowLeft size={14} /> Back to the Crypt
            </a>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-5xl px-4 sm:px-6 py-16 sm:py-24">
        {!orders ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-md mx-auto border border-blooddeep/50 bg-pulp p-8 sm:p-10 text-center"
          >
            <Skull size={40} className="mx-auto text-crimson crimson-glow mb-6" />
            <h1 className="font-display text-2xl sm:text-3xl uppercase text-paper mb-3">
              THE <span className="text-crimson">LEDGER</span>
            </h1>
            <p className="font-type text-sm text-paper/60 mb-8">
              Orders live here. Speak the key and enter.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                load(input);
              }}
              className="space-y-4"
            >
              <input
                data-testid="admin-key-input"
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ledger key"
                className="w-full font-courier text-sm bg-void border-2 border-input text-paper px-4 py-3.5 text-center focus:outline-none focus:border-crimson focus:ring-1 focus:ring-crimson placeholder:text-paper/30"
              />
              <button
                data-testid="admin-login-button"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 font-impact uppercase tracking-wider bg-blooddeep text-white border-2 border-black px-6 py-4 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 disabled:opacity-50"
              >
                <KeyRound size={16} /> {loading ? "Turning the key..." : "Open the Ledger"}
              </button>
            </form>
            {user && user.role === "admin" && (
              <button
                data-testid="admin-enter-as-user-button"
                onClick={() => load("")}
                disabled={loading}
                className="mt-4 w-full flex items-center justify-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 border border-paper/30 px-6 py-3 transition-colors duration-150 hover:border-crimson hover:text-crimson disabled:opacity-50"
              >
                <Skull size={14} /> Enter as {user.email}
              </button>
            )}
          </motion.div>
        ) : (
          <>
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="font-marker text-bloodglow text-base mb-2 -rotate-1 inline-block">
                  every order, in blood-red ink
                </p>
                <h1 className="font-display text-3xl sm:text-4xl uppercase text-paper">
                  ORDER <span className="text-crimson">LEDGER</span>
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-wrap border border-blooddeep/50">
                  <button
                    data-testid="admin-tab-dashboard"
                    onClick={() => setView("dashboard")}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "dashboard" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Dashboard
                  </button>
                  <button
                    data-testid="admin-tab-orders"
                    onClick={() => setView("orders")}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "orders" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Orders
                  </button>
                  <button
                    data-testid="admin-tab-products"
                    onClick={() => {
                      setView("products");
                      if (!products) loadProducts();
                    }}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "products" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Products
                  </button>
                  <button
                    data-testid="admin-tab-customers"
                    onClick={() => setView("customers")}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "customers" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Customers
                  </button>
                  <button
                    data-testid="admin-tab-marketing"
                    onClick={() => setView("marketing")}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "marketing" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Marketing
                  </button>
                  <button
                    data-testid="admin-tab-dossiers"
                    onClick={() => setView("dossiers")}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "dossiers" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Dossiers
                  </button>
                  <button
                    data-testid="admin-tab-admins"
                    onClick={() => {
                      setView("admins");
                      if (!admins) loadAdmins();
                    }}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${view === "admins" ? "bg-crimson text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Admins
                  </button>
                </div>
                {view === "orders" && (
                <div className="flex border border-blooddeep/50">
                  <button
                    data-testid="admin-filter-all"
                    onClick={() => setPackingOnly(false)}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${!packingOnly ? "bg-blooddeep text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    All Orders
                  </button>
                  <button
                    data-testid="admin-filter-packing"
                    onClick={() => setPackingOnly(true)}
                    className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 transition-colors duration-150 ${packingOnly ? "bg-blooddeep text-white" : "text-paper/60 hover:text-paper"}`}
                  >
                    Packing Queue{packingCount > 0 ? ` (${packingCount})` : ""}
                  </button>
                </div>
                )}
                <button
                  data-testid="admin-refresh-button"
                  onClick={() => load(key)}
                  className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 border border-paper/30 px-4 py-2.5 transition-colors duration-150 hover:border-crimson hover:text-crimson"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>

            {view === "dashboard" ? (
              <AdminDashboard cfg={adminCfg} />
            ) : view === "customers" ? (
              <AdminCustomers cfg={adminCfg} />
            ) : view === "marketing" ? (
              <AdminMarketing cfg={adminCfg} />
            ) : view === "dossiers" ? (
              <AdminDossiers cfg={adminCfg} />
            ) : view === "products" ? (
              <div data-testid="admin-products-panel">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                  <p className="font-type text-sm text-paper/60">
                    Drop a price or add an issue — every fan who hearted it gets an email in blood-red ink. Drag rows to reorder the shop.
                  </p>
                  <button
                    data-testid="admin-add-product-toggle"
                    onClick={() => setShowAdd(!showAdd)}
                    className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
                  >
                    <Plus size={14} /> New Product
                  </button>
                </div>

                {showAdd && (
                  <form data-testid="admin-add-form" onSubmit={addProduct} className="border border-blooddeep/50 bg-pulp p-6 mb-8 grid sm:grid-cols-2 gap-3">
                    <input data-testid="admin-add-title" value={addForm.title} onChange={(e) => setAddForm({ ...addForm, title: e.target.value })} placeholder="Title (e.g. Mary Machinegun #2)" className={adminInput} required />
                    <input data-testid="admin-add-price" type="number" step="0.01" min="0.01" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value })} placeholder="Price" className={adminInput} required />
                    <select data-testid="admin-add-category" value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} className={adminInput}>
                      <option value="physical">Physical</option>
                      <option value="digital">Digital</option>
                      <option value="collectible">Collectible</option>
                      <option value="bundle">Bundle</option>
                      <option value="giftcard">Gift Card</option>
                      <option value="mystery">Mystery Coffin</option>
                    </select>
                    <input data-testid="admin-add-badge" value={addForm.badge} onChange={(e) => setAddForm({ ...addForm, badge: e.target.value })} placeholder="Badge (e.g. NEW ISSUE)" className={adminInput} />
                    <label className="flex items-center gap-2 font-courier text-xs text-paper/60 cursor-pointer">
                      <input
                        data-testid="admin-add-preorder"
                        type="checkbox"
                        checked={addForm.preorder}
                        onChange={(e) => setAddForm({ ...addForm, preorder: e.target.checked })}
                        className="accent-crimson w-4 h-4"
                      />
                      Pre-order (buyers pay now, ships later)
                    </label>
                    <label className="block">
                      <span className="font-courier text-[10px] uppercase tracking-widest text-paper/40 block mb-1">Ships by (pre-orders)</span>
                      <input data-testid="admin-add-ships-by" type="date" value={addForm.ships_by} onChange={(e) => setAddForm({ ...addForm, ships_by: e.target.value })} className={adminInput} />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="font-courier text-[10px] uppercase tracking-widest text-paper/40 block mb-1">Release date (shows homepage countdown — leave empty to hide)</span>
                      <input data-testid="admin-add-release-at" type="datetime-local" value={addForm.release_at} onChange={(e) => setAddForm({ ...addForm, release_at: e.target.value })} className={adminInput} />
                    </label>
                    {addForm.release_at && (
                      <label className="flex items-center gap-2 font-courier text-xs text-paper/60 cursor-pointer sm:col-span-2">
                        <input
                          data-testid="admin-add-hidden"
                          type="checkbox"
                          checked={addForm.hidden || false}
                          onChange={(e) => setAddForm({ ...addForm, hidden: e.target.checked })}
                          className="accent-crimson w-4 h-4"
                        />
                        Hide until release — scheduled drop: auto-publishes and alerts series fans at the exact time
                      </label>
                    )}
                    {addForm.category === "bundle" && (
                      <div data-testid="admin-add-bundle-picker" className="sm:col-span-2 border-2 border-dashed border-yell/40 bg-void px-4 py-3">
                        <p className="font-courier text-xs text-paper/60 mb-2">Bundle guts — tick everything inside:</p>
                        <div className="grid sm:grid-cols-2 gap-1.5 max-h-44 overflow-y-auto">
                          {(products || []).map((p) => (
                            <label key={p.id} className="flex items-center gap-2 font-courier text-[11px] text-paper/60 cursor-pointer">
                              <input
                                data-testid={`bundle-item-${p.id}`}
                                type="checkbox"
                                checked={addForm.bundle_items.includes(p.id)}
                                onChange={(e) =>
                                  setAddForm({
                                    ...addForm,
                                    bundle_items: e.target.checked
                                      ? [...addForm.bundle_items, p.id]
                                      : addForm.bundle_items.filter((id) => id !== p.id),
                                  })
                                }
                                className="accent-crimson w-3.5 h-3.5"
                              />
                              {p.title} <span className="text-paper/35">({p.format})</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <label
                      data-testid="admin-add-cover-label"
                      className="sm:col-span-2 flex items-center gap-4 border-2 border-dashed border-blooddeep/60 bg-void px-4 py-3 cursor-pointer transition-colors duration-150 hover:border-crimson"
                    >
                      {coverPreview ? (
                        <img src={coverPreview} alt="Cover preview" className="w-12 h-[72px] object-cover border border-black" />
                      ) : (
                        <Upload size={20} className="text-crimson shrink-0" />
                      )}
                      <span className="font-courier text-xs text-paper/60">
                        {coverFile ? coverFile.name : "Choose a cover image from your computer (JPG, PNG, WebP — 5 MB max)"}
                      </span>
                      <input
                        data-testid="admin-add-cover-file"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setCoverFile(f || null);
                          setCoverPreview((old) => {
                            if (old) URL.revokeObjectURL(old);
                            return f ? URL.createObjectURL(f) : null;
                          });
                        }}
                      />
                    </label>
                    <textarea data-testid="admin-add-description" rows={2} value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="Description" className={`${adminInput} sm:col-span-2 resize-none`} required />
                    {addForm.category === "digital" && (
                      <label
                        data-testid="admin-add-pdf-label"
                        className="sm:col-span-2 flex items-center gap-4 border-2 border-dashed border-cyanx/40 bg-void px-4 py-3 cursor-pointer transition-colors duration-150 hover:border-cyanx"
                      >
                        <Paperclip size={20} className="text-cyanx shrink-0" />
                        <span className="font-courier text-xs text-paper/60">
                          {pdfFile ? pdfFile.name : "Attach the comic PDF buyers download (PDF — 50 MB max; optional, can add later from the product row)"}
                        </span>
                        <input
                          data-testid="admin-add-pdf-file"
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                        />
                      </label>
                    )}
                    {addForm.category === "digital" && (
                      <label
                        data-testid="admin-add-previews-label"
                        className="sm:col-span-2 flex items-center gap-4 border-2 border-dashed border-yell/40 bg-void px-4 py-3 cursor-pointer transition-colors duration-150 hover:border-yell"
                      >
                        <Upload size={20} className="text-yell shrink-0" />
                        <span className="font-courier text-xs text-paper/60">
                          {previewFiles.length > 0
                            ? `${previewFiles.length} preview page${previewFiles.length === 1 ? "" : "s"} chosen`
                            : "Free preview pages fans can read before buying (JPG/PNG/WebP — up to 6 pages, optional)"}
                        </span>
                        <input
                          data-testid="admin-add-preview-files"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          className="hidden"
                          onChange={(e) => setPreviewFiles(Array.from(e.target.files || []).slice(0, 6))}
                        />
                      </label>
                    )}
                    <button data-testid="admin-add-submit" type="submit" className="sm:col-span-2 font-impact uppercase tracking-wider bg-blooddeep text-white border-2 border-black px-6 py-3 ink-shadow-sm transition-colors duration-150 hover:bg-crimson">
                      Add to the Rack
                    </button>
                  </form>
                )}

                <div className="space-y-4">
                  {(products || []).map((p) => (
                    <div
                      key={p.id}
                      data-testid={`admin-product-${p.id}`}
                      draggable
                      onDragStart={() => (dragId.current = p.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDropRow(p.id)}
                      className={`border p-4 ${p.retired ? "border-paper/20 bg-void/60 opacity-60" : "border-blooddeep/40 bg-pulp/60"}`}
                    >
                      {editingId === p.id ? (
                        <div className="space-y-3">
                          <div className="grid sm:grid-cols-2 gap-3">
                            <input
                              data-testid="admin-edit-title"
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                              placeholder="Title"
                              className={adminInput}
                            />
                            <input
                              data-testid="admin-edit-badge"
                              value={editForm.badge}
                              onChange={(e) => setEditForm({ ...editForm, badge: e.target.value })}
                              placeholder="Badge"
                              className={adminInput}
                            />
                          </div>
                          <textarea
                            data-testid="admin-edit-description"
                            rows={2}
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Description"
                            className={`${adminInput} w-full resize-none`}
                          />
                          <div className="flex gap-3">
                            <button
                              data-testid="admin-edit-save"
                              onClick={() => saveEdit(p.id)}
                              className="font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-4 py-2 ink-shadow-sm transition-colors duration-150 hover:bg-crimsonhot"
                            >
                              Save Edits
                            </button>
                            <button
                              data-testid="admin-edit-cancel"
                              onClick={() => setEditingId(null)}
                              className="font-impact uppercase tracking-wider text-xs text-paper/60 border border-paper/30 px-4 py-2 transition-colors duration-150 hover:text-paper"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-4">
                          <GripVertical size={16} className="text-paper/30 cursor-grab shrink-0" />
                          <label
                            data-testid={`admin-cover-swap-label-${p.id}`}
                            className="relative cursor-pointer group/cover shrink-0"
                            title="Swap cover image"
                          >
                            <img
                              src={p.cover}
                              alt={p.title}
                              className="w-12 h-[72px] object-cover border border-black transition-opacity duration-150 group-hover/cover:opacity-40"
                            />
                            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/cover:opacity-100 transition-opacity duration-150">
                              <Upload size={16} className="text-crimson" />
                            </span>
                            <input
                              data-testid={`admin-cover-swap-${p.id}`}
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                swapCover(p.id, e.target.files?.[0]);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <div className="flex-1 min-w-[160px]">
                            <p className="font-impact uppercase tracking-wide text-sm text-paper">
                              {p.title}
                              {p.retired && (
                                <span data-testid={`admin-retired-tag-${p.id}`} className="ml-2 font-marker text-[10px] text-paper/50 border border-paper/30 px-2 py-0.5 align-middle">
                                  RETIRED
                                </span>
                              )}
                              {!p.retired && p.stock != null && p.stock <= 5 && (p.category === "physical" || p.category === "collectible") && (
                                <span data-testid={`admin-lowstock-tag-${p.id}`} className="ml-2 font-marker text-[10px] text-yell border border-yell/50 px-2 py-0.5 align-middle">
                                  LOW STOCK
                                </span>
                              )}
                            </p>
                            <p className="font-courier text-[10px] uppercase tracking-widest text-paper/40 mt-0.5">
                              {p.format} — {p.badge}
                            </p>
                          </div>
                          {p.retired ? (
                            <button
                              data-testid={`admin-restore-${p.id}`}
                              onClick={() => retire(p.id, false)}
                              className="font-impact uppercase tracking-wider text-xs text-paper/70 border border-paper/30 px-4 py-2 transition-colors duration-150 hover:border-crimson hover:text-crimson"
                            >
                              Restore
                            </button>
                          ) : (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-courier text-xs text-paper/50">$</span>
                              <input
                                data-testid={`admin-price-input-${p.id}`}
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={priceInputs[p.id] ?? ""}
                                onChange={(e) => setPriceInputs((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                className="w-24 font-courier text-sm bg-void border-2 border-input text-paper px-2 py-2 focus:outline-none focus:border-crimson"
                              />
                              <button
                                data-testid={`admin-price-save-${p.id}`}
                                onClick={() => savePrice(p.id)}
                                className="font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-4 py-2 ink-shadow-sm transition-colors duration-150 hover:bg-crimsonhot"
                              >
                                Save
                              </button>
                              {(p.category === "physical" || p.category === "collectible") && (
                                <>
                                  <span className="font-courier text-xs text-paper/50">STK</span>
                                  <input
                                    data-testid={`admin-stock-input-${p.id}`}
                                    type="number"
                                    min="0"
                                    value={stockInputs[p.id] ?? ""}
                                    onChange={(e) => setStockInputs((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                    placeholder="∞"
                                    title="Stock count — blank means unlimited"
                                    className="w-16 font-courier text-sm bg-void border-2 border-input text-paper px-2 py-2 focus:outline-none focus:border-crimson"
                                  />
                                  <button
                                    data-testid={`admin-stock-save-${p.id}`}
                                    onClick={() => saveStock(p.id)}
                                    className="font-impact uppercase tracking-wider text-xs text-paper/70 border border-paper/30 px-3 py-2 transition-colors duration-150 hover:border-crimson hover:text-crimson"
                                  >
                                    Set
                                  </button>
                                </>
                              )}
                              <button
                                data-testid={`admin-edit-${p.id}`}
                                onClick={() => {
                                  setEditingId(p.id);
                                  setEditForm({ title: p.title, badge: p.badge, description: p.description });
                                }}
                                className="font-impact uppercase tracking-wider text-xs text-paper/70 border border-paper/30 px-4 py-2 transition-colors duration-150 hover:border-crimson hover:text-crimson"
                              >
                                Edit
                              </button>
                              {p.category === "digital" && (
                                <label
                                  data-testid={`admin-file-label-${p.id}`}
                                  title={p.file_path ? `${p.file_name || "PDF"} attached — click to replace` : "Attach the downloadable PDF buyers receive"}
                                  className={`flex items-center gap-1.5 font-impact uppercase tracking-wider text-xs border px-4 py-2 cursor-pointer transition-colors duration-150 ${
                                    p.file_path
                                      ? "text-cyanx border-cyanx/40"
                                      : "text-paper/70 border-paper/30 hover:border-crimson hover:text-crimson"
                                  }`}
                                >
                                  <Paperclip size={13} /> {p.file_path ? "PDF ✓" : "PDF"}
                                  <input
                                    data-testid={`admin-file-attach-${p.id}`}
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      attachFile(p.id, e.target.files?.[0]);
                                      e.target.value = "";
                                    }}
                                  />
                                </label>
                              )}
                              <button
                                data-testid={`admin-retire-${p.id}`}
                                onClick={() => {
                                  if (retiringId === p.id) {
                                    retire(p.id, true);
                                  } else {
                                    setRetiringId(p.id);
                                    setTimeout(() => setRetiringId((cur) => (cur === p.id ? null : cur)), 4000);
                                  }
                                }}
                                className={`font-impact uppercase tracking-wider text-xs border px-4 py-2 transition-colors duration-150 ${
                                  retiringId === p.id
                                    ? "bg-blooddeep text-white border-black"
                                    : "text-bloodglow border-blooddeep/50 hover:border-crimson"
                                }`}
                              >
                                {retiringId === p.id ? "Sure?" : "Retire"}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : view === "admins" ? (
              <div data-testid="admin-admins-panel">
                <p className="font-type text-sm text-paper/60 mb-6">
                  Admins can open this ledger with their own account — no key needed. Hand out keys carefully; each one can crown more admins.
                </p>
                <form data-testid="admin-add-admin-form" onSubmit={addAdmin} className="border border-blooddeep/50 bg-pulp p-6 mb-8 grid sm:grid-cols-3 gap-3">
                  <input data-testid="admin-add-admin-name" value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} placeholder="Name" className={adminInput} required />
                  <input data-testid="admin-add-admin-email" type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} placeholder="Email" className={adminInput} required />
                  <input data-testid="admin-add-admin-password" type="password" minLength={8} value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="Temp password (8+ chars)" className={adminInput} required />
                  <div className="sm:col-span-3 flex flex-wrap items-center gap-3">
                    <button
                      data-testid="admin-add-admin-submit"
                      disabled={addingAdmin}
                      className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson disabled:opacity-50"
                    >
                      <UserPlus size={14} /> {addingAdmin ? "Crowning..." : "Crown Admin"}
                    </button>
                    <p className="font-courier text-[11px] text-paper/40">
                      New admins should log in at /account and change their password immediately.
                    </p>
                  </div>
                </form>
                <div data-testid="admin-admins-list" className="space-y-3">
                  {(admins || []).map((a) => (
                    <div key={a.id} data-testid={`admin-row-${a.email}`} className="border border-blooddeep/40 bg-pulp px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-impact uppercase tracking-wide text-sm text-paper">{a.name}</p>
                        <p className="font-courier text-[11px] text-paper/50">{a.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-courier text-[10px] uppercase tracking-widest text-paper/40">
                          Crowned {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                        </p>
                        {user?.email !== a.email && (
                          <button
                            data-testid={`admin-revoke-${a.email}`}
                            onClick={() => {
                              if (revokingId === a.id) {
                                revokeAdmin(a);
                              } else {
                                setRevokingId(a.id);
                                setTimeout(() => setRevokingId((cur) => (cur === a.id ? null : cur)), 4000);
                              }
                            }}
                            className={`font-impact uppercase tracking-wider text-xs border px-4 py-2 transition-colors duration-150 ${
                              revokingId === a.id
                                ? "bg-blooddeep text-white border-black"
                                : "text-bloodglow border-blooddeep/50 hover:border-crimson"
                            }`}
                          >
                            {revokingId === a.id ? "Sure?" : "Revoke"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {admins && admins.length === 0 && (
                    <p className="font-type text-sm text-paper/50">No admin accounts yet.</p>
                  )}
                </div>
              </div>
            ) : sorted.length === 0 ? (
              <p data-testid="admin-empty" className="font-type text-paper/50 text-center py-16">
                No orders yet. The coffin waits.
              </p>
            ) : (
              <div data-testid="admin-orders-list" className="space-y-5">
                {sorted.map((o) => (
                  <motion.article
                    key={o.id}
                    data-testid={`order-row-${o.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`border p-6 ${o.contains_physical && o.status === "paid" ? "border-crimson bg-pulp" : "border-blooddeep/40 bg-pulp/60"}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <p className="font-impact uppercase tracking-wide text-paper text-lg">
                          {o.customer_name}
                        </p>
                        <p className="font-courier text-xs text-bloodglow mt-0.5 flex items-center gap-1.5">
                          <Mail size={11} /> {o.customer_email}
                        </p>
                        <p className="font-courier text-[10px] uppercase tracking-widest text-paper/40 mt-1">
                          {new Date(o.created_at).toLocaleString()} — {o.id.slice(0, 8)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {o.contains_physical && (
                          <span className="bg-blooddeep text-white font-marker text-xs px-3 py-1 -rotate-2 ink-shadow-sm">
                            PHYSICAL — MAIL IT
                          </span>
                        )}
                        <span
                          data-testid={`order-status-${o.id}`}
                          className={`font-courier text-[10px] uppercase tracking-widest px-2.5 py-1 border ${o.status === "shipped" ? "text-paper/60 border-paper/30" : "text-bloodglow border-blooddeep/50"}`}
                        >
                          {o.status}
                        </span>
                        <span className="font-marker text-bone text-xl">${o.total.toFixed(2)}</span>
                      </div>
                    </div>
                    <ul className="font-type text-sm text-paper/70 space-y-1 mb-4">
                      {o.items.map((i) => (
                        <li key={i.product_id}>
                          {i.title} <span className="text-paper/40">× {i.qty}</span>
                          {i.mystery_picks && (
                            <span data-testid={`mystery-picks-${o.id}`} className="block font-courier text-[11px] text-yell mt-0.5">
                              crypt chose: {i.mystery_picks.map((p) => p.title).join(" + ")}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {o.contains_physical && (
                      <p className="font-courier text-xs text-paper/60 border-t border-blooddeep/30 pt-4 whitespace-pre-line">
                        <span className="uppercase tracking-widest text-[10px] text-paper/40 block mb-1">Ship to</span>
                        {o.shipping_address || "No address on file — email the buyer."}
                      </p>
                    )}
                    {o.tracking && (
                      <p className="font-courier text-xs text-paper/60 mt-2">
                        <span className="uppercase tracking-widest text-[10px] text-paper/40 block mb-1">Tracking</span>
                        <span className="text-bloodglow">{o.tracking}</span>
                      </p>
                    )}
                    {(o.admin_note || noteEditing === o.id) && noteEditing !== o.id && (
                      <p data-testid={`order-note-${o.id}`} className="font-courier text-xs text-yell/80 mt-2">
                        <span className="uppercase tracking-widest text-[10px] text-paper/40 block mb-1">Ledger note</span>
                        {o.admin_note}
                      </p>
                    )}
                    {noteEditing === o.id ? (
                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <input
                          data-testid={`note-input-${o.id}`}
                          value={noteInput}
                          onChange={(e) => setNoteInput(e.target.value)}
                          placeholder="Private note (e.g. paid cash at con)"
                          maxLength={1000}
                          className="flex-1 font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30"
                        />
                        <button
                          data-testid={`note-save-${o.id}`}
                          onClick={() => saveNote(o.id)}
                          className="font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimsonhot"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setNoteEditing(null)}
                          className="font-impact uppercase tracking-wider text-xs text-paper/60 border border-paper/30 px-4 py-2.5 transition-colors duration-150 hover:text-paper"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : null}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {noteEditing !== o.id && (
                        <button
                          data-testid={`note-toggle-${o.id}`}
                          onClick={() => { setNoteEditing(o.id); setNoteInput(o.admin_note || ""); }}
                          className="font-impact uppercase tracking-wider text-[11px] text-paper/60 border border-paper/30 px-3 py-2 transition-colors duration-150 hover:border-cyanx hover:text-cyanx"
                        >
                          {o.admin_note ? "Edit Note" : "Add Note"}
                        </button>
                      )}
                      {(o.status === "paid" || o.status === "shipped") && (
                        <button
                          data-testid={`refund-${o.id}`}
                          onClick={() => {
                            if (refundingId === o.id) {
                              refundOrder(o.id);
                            } else {
                              setRefundingId(o.id);
                              setTimeout(() => setRefundingId((cur) => (cur === o.id ? null : cur)), 4000);
                            }
                          }}
                          className={`font-impact uppercase tracking-wider text-[11px] border px-3 py-2 transition-colors duration-150 ${
                            refundingId === o.id
                              ? "bg-blooddeep text-white border-black"
                              : "text-bloodglow border-blooddeep/50 hover:border-crimson hover:text-crimson"
                          }`}
                        >
                          {refundingId === o.id ? "Sure? Money goes back" : "Refund"}
                        </button>
                      )}
                      {o.status === "refunded" && (
                        <span data-testid={`refunded-tag-${o.id}`} className="font-marker text-[11px] text-paper/40 border border-paper/30 px-2 py-0.5 rotate-2">
                          REFUNDED
                        </span>
                      )}
                    </div>
                    {o.contains_physical && o.status === "paid" && (
                      shippingId === o.id ? (
                        <div className="mt-4 flex flex-col sm:flex-row gap-3">
                          <input
                            data-testid={`tracking-input-${o.id}`}
                            value={trackingInput}
                            onChange={(e) => setTrackingInput(e.target.value)}
                            placeholder="Tracking number (optional)"
                            className="flex-1 font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30"
                          />
                          <button
                            data-testid={`confirm-shipped-${o.id}`}
                            onClick={() => markShipped(o.id)}
                            className="flex items-center justify-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
                          >
                            <PackageCheck size={14} /> Confirm Shipped
                          </button>
                          <button
                            data-testid={`cancel-shipped-${o.id}`}
                            onClick={() => { setShippingId(null); setTrackingInput(""); }}
                            className="font-impact uppercase tracking-wider text-xs text-paper/60 border border-paper/30 px-4 py-2.5 transition-colors duration-150 hover:text-paper"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`mark-shipped-${o.id}`}
                          onClick={() => { setShippingId(o.id); setTrackingInput(""); }}
                          className="mt-4 flex items-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
                        >
                          <PackageCheck size={14} /> Mark Shipped
                        </button>
                      )
                    )}
                  </motion.article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
